import { put, all, takeLatest, select } from "redux-saga/effects";
import * as actionTypes from "./actionTypes";
import * as extractionsActionTypes from "../extractions/actionTypes";
import API from "../../commonUtils/API.js";
import { toast } from "react-toastify";
import { handleApiError } from "../../commonUtils/Functions";
import { parseBackendErrors } from "../../commonUtils/queryBuilder.helper.js";
import { setQBErrors } from "../extractions/action";


function* getExtDataGroupsSaga({ params }) {
    const { extId, keyword, versionId, is_draft } = params;
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        let queryString = '';
        if (keyword)              queryString += `&keyword=${encodeURIComponent(keyword)}`;
        if (versionId)            queryString += `&versionId=${versionId}`;
        if (is_draft !== undefined && is_draft !== null) queryString += `&is_draft=${is_draft}`;

        const { data } = yield API.get(`/ext/${extId}/data-groups?${queryString}`);
        if (data.success) {
            yield put({ type: actionTypes.GET_EXT_DATA_GROUPS_SUCCESS, payload: data.data });
        } else {
            toast.error("Oops! Something went unexpectedly wrong.");
            yield put({ type: actionTypes.GET_EXT_DATA_GROUPS_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Oops! Something went unexpectedly wrong.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_EXT_DATA_GROUPS_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* getExtDataGroupSaga({ id }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const extractionData = yield select((state) => state.Extractions.extractionData);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.GET_EXT_DATA_GROUP_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }

        const { data } = yield API.get(`/ext/${extractionData.id}/data-groups/${id}`);
        if (data.success) {
            yield put({ type: actionTypes.GET_EXT_DATA_GROUP_SUCCESS, payload: data.data });
        } else {
            toast.error("Could not load data group.");
            yield put({ type: actionTypes.GET_EXT_DATA_GROUP_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not load data group.");
        const data = error?.response?.data;
        yield put({ type: actionTypes.GET_EXT_DATA_GROUP_FAILURE, payload: data || { message: "Internal server error" } });
    }
}

function* createExtDataGroupSaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        
        const { formData } = payload;
        const extractionData = yield select((state) => state.Extractions.extractionData);
        
        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.CREATE_EXT_DATA_GROUP_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }

        const { data } = yield API.post(`/ext/${extractionData.id}/data-groups`, formData);
        
        if (data.success) {
            yield put(setQBErrors({}));
            yield put({ 
                type: actionTypes.CREATE_EXT_DATA_GROUP_SUCCESS, 
                payload: data.data 
            });
            
            // Read filters from ExtDataGroups slice, not Extractions
            const filters = yield select((state) => state.ExtDataGroups.filters);
            yield put({ type: actionTypes.GET_EXT_DATA_GROUPS, params: { extId: extractionData?.id, ...filters } });
        } else {
            if (data?.data) {
                const parsedErrors = parseBackendErrors(data.data);
                yield put(setQBErrors(parsedErrors));
                toast.error("Please fix validation errors");
            } else {
                toast.error(data?.message || "Could not save data group.");
            }
            yield put({ 
                type: actionTypes.CREATE_EXT_DATA_GROUP_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        const errorData = error?.response?.data;
        if (errorData?.data) {
            const parsedErrors = parseBackendErrors(errorData.data);
            yield put(setQBErrors(parsedErrors));
            toast.error("Please fix validation errors");
        } else {
            handleApiError(error, "Could not save data group.");
        }
        yield put({ 
            type: actionTypes.CREATE_EXT_DATA_GROUP_FAILURE, 
            payload: { 
                message: errorData?.message || "Internal server error",
                errors: errorData?.data || null
            } 
        });
    }
};

function* updateExtDataGroupSaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });
        
        const { id, formData } = payload;
        const extractionData = yield select((state) => state.Extractions.extractionData);

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.UPDATE_EXT_DATA_GROUP_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }
        
        const { data } = yield API.put(`/ext/${extractionData.id}/data-groups/${id}`, formData);
        
        if (data.success) {
            yield put(setQBErrors({}));
            yield put({ 
                type: actionTypes.UPDATE_EXT_DATA_GROUP_SUCCESS, 
                payload: data.data 
            });
            
            // Read filters from ExtDataGroups slice, not Extractions
            const filters = yield select((state) => state.ExtDataGroups.filters);
            yield put({ type: actionTypes.GET_EXT_DATA_GROUPS, params: { extId: extractionData?.id, ...filters } });
        } else {
            if (data?.data) {
                const parsedErrors = parseBackendErrors(data.data);
                yield put(setQBErrors(parsedErrors));
                toast.error("Please fix validation errors");
            } else {
                toast.error(data?.message || "Could not save updated data group data.");
            }
            yield put({ 
                type: actionTypes.UPDATE_EXT_DATA_GROUP_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        const errorData = error?.response?.data;
        if (errorData?.data) {
            const parsedErrors = parseBackendErrors(errorData.data);
            yield put(setQBErrors(parsedErrors));
            toast.error("Please fix validation errors");
        } else {
            handleApiError(error, "Could not save updated data group data.");
        }
        yield put({ 
            type: actionTypes.UPDATE_EXT_DATA_GROUP_FAILURE, 
            payload: { 
                message: errorData?.message || "Internal server error",
                errors: errorData?.data || null
            } 
        });
    }
};

function* updateExtDataGroupsOrderSaga({ payload }) {
    const extractionData = yield select((state) => state.Extractions.extractionData);
    const filters = yield select((state) => state.ExtDataGroups.filters);
    try {
        const { orderData } = payload;

        if (!extractionData?.id) {
            toast.error("Extraction ID not found. Please refresh the page.");
            yield put({ 
                type: actionTypes.UPDATE_EXT_DATA_GROUPS_ORDER_FAILURE, 
                payload: { message: "Extraction ID not found" } 
            });
            return;
        }

        const { data } = yield API.put(
            `/ext/${extractionData.id}/data-groups/update-order`, 
            { orderData }
        );

        if (data.success) {
            yield put({ 
                type: actionTypes.UPDATE_EXT_DATA_GROUPS_ORDER_SUCCESS, 
                payload: data.data 
            });
        } else {
            toast.error(data?.message || "Could not update order.");
            yield put({ 
                type: actionTypes.UPDATE_EXT_DATA_GROUPS_ORDER_FAILURE, 
                payload: data 
            });
        }

    } catch (error) {
        handleApiError(error, "Could not update order.");
        const data = error?.response?.data;
        toast.error("Failed to update order. Refreshing...");
        yield put({ type: actionTypes.GET_EXT_DATA_GROUPS, params: { extId: extractionData?.id, ...filters } });
        yield put({ 
            type: actionTypes.UPDATE_EXT_DATA_GROUPS_ORDER_FAILURE, 
            payload: data || { message: "Internal server error" } 
        });
    }
};

function* deleteExtDataGroupSaga({ Id }) {
    try {
        const extractionData = yield select((state) => state.Extractions.extractionData);
        const filters = yield select((state) => state.ExtDataGroups.filters);
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { data } = yield API.delete(`/ext/${extractionData.id}/data-groups/${Id}`);

        if (data.success) {
            yield put({ type: actionTypes.DELETE_EXT_DATA_GROUP_SUCCESS, payload: data });
            yield put({ type: actionTypes.GET_EXT_DATA_GROUPS, params: { extId: extractionData?.id, ...filters } });
        } else {
            toast.error(data?.message || "Could not delete data group.");
            yield put({ type: actionTypes.DELETE_EXT_DATA_GROUP_FAILURE, payload: data });
        }

    } catch (error) {
        handleApiError(error, "Could not delete data group.");
        const data = error?.response?.data;
        toast.error(data?.message || "Could not delete data group.");
        yield put({ type: actionTypes.DELETE_EXT_DATA_GROUP_FAILURE, payload: data || { message: "Internal server error" } });
    }
};

function* setOpenModalSaga({ payload }) {
    if (!payload) {
        yield put({ type: extractionsActionTypes.RESET_QUERY_BUILDER });
    }
}

function* ExtDataGroupsSaga() {
  yield all([
    takeLatest(actionTypes.GET_EXT_DATA_GROUPS, getExtDataGroupsSaga),
    takeLatest(actionTypes.GET_EXT_DATA_GROUP, getExtDataGroupSaga),
    takeLatest(actionTypes.CREATE_EXT_DATA_GROUP, createExtDataGroupSaga),
    takeLatest(actionTypes.UPDATE_EXT_DATA_GROUP, updateExtDataGroupSaga),
    takeLatest(actionTypes.UPDATE_EXT_DATA_GROUPS_ORDER, updateExtDataGroupsOrderSaga),
    takeLatest(actionTypes.DELETE_EXT_DATA_GROUP, deleteExtDataGroupSaga),
    takeLatest(actionTypes.SET_OPEN_MODAL, setOpenModalSaga)
  ]);
}

export default ExtDataGroupsSaga;